import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useBoards } from '../../hooks/useBoards'
import { Button } from '../common/Button'
import { LoadingSpinner } from '../common/LoadingSpinner'
import { BoardCard } from './BoardCard'
import './BoardList.css'
import { CreateBoardModal } from './CreateBoardModal'

export interface BoardListProps {
  workspaceId: number
}

/**
 * BoardList Component
 * Displays all boards in a workspace
 * Implements Requirement 4.1: Board creation succeeds for workspace members
 */
export const BoardList: React.FC<BoardListProps> = ({ workspaceId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: boards, isLoading, error } = useBoards(workspaceId)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const handleBoardClick = (boardId: number) => {
    navigate(`/boards/${boardId}`)
  }

  const handleCreateClick = () => {
    setIsCreateModalOpen(true)
  }

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false)
  }

  if (isLoading) {
    return (
      <div className="board-list__loading">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="board-list__error">
        <p>{t('board.failedToLoad')}</p>
      </div>
    )
  }

  // Filter out archived boards
  const activeBoards = boards?.filter((board) => !board.archived) || []

  return (
    <div className="board-list">
      <div className="board-list__header">
        <h1 className="board-list__title">{t('board.title')}</h1>
        <Button onClick={handleCreateClick}>{t('board.create')}</Button>
      </div>

      {activeBoards.length > 0 ? (
        <div className="board-list__grid">
          {activeBoards.map((board) => (
            <BoardCard
              key={board.id}
              board={board}
              onClick={() => handleBoardClick(board.id)}
            />
          ))}
        </div>
      ) : (
        <div className="board-list__empty">
          <p>{t('board.noBoards')}</p>
          <Button onClick={handleCreateClick}>{t('board.createBoard')}</Button>
        </div>
      )}

      <CreateBoardModal
        workspaceId={workspaceId}
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  )
}
